import { useTranslation } from "react-i18next";
import React, { useEffect, useRef, useState } from "react";
import { Button, Flex, Spin, Tooltip } from "antd";

import "react-h5-audio-player/lib/styles.css";
import "./style.scss";
import { isEmpty, isNumber, toNumber } from "lodash";
import H5AudioPlayer from "react-h5-audio-player";
import {
  ExportIcon,
  HoursGlassIcon,
  MicroIcon,
  PausePrimaryIcon,
  PlayIcon,
  QuestionMarkIcon,
} from "../../../../../../../components/icons";
import EmptyComponent from "../../../../../../../components/empty";
import { QuestionProps, QuizProps } from "../../../../../../../types/course";
import { FormInstance } from "antd/lib";
import {
  JSON_SUB_TYPE,
  MAX_QUIZ_FILE_UPLOAD_SIZE,
  QuestionTypeEnum,
} from "../../../../../../../constants/course";
import Countdown from "../../../../../../../components/countdown/CountDown";
import useKeyword from "../../../../../../../hooks/useKeyword";
import { startSingleQuiz } from "../../../../../../../service/learning-course";
import { Link } from "react-router-dom";
import { uploadFileV2 } from "../../../../../../../service/uploadFile/infoDetailApi";
import QuestionContentTitle from "../../components/QuestionContentTitle";

interface Mp3Props extends QuestionProps {
  data?: QuizProps;
  form?: FormInstance<any>;
  onChange?: (value: any) => void;
}

function Mp3({
  data,
  disabled,
  initialData,
  pause,
  configs,
  hiddenIndex,
  onFinish = () => {},
  onChange = () => {},
}: Mp3Props) {
  const { t } = useTranslation();
  const initialTime =
    data?.timeToCompleted || configs?.actionConfig?.timeForQuiz || 0;
  const maxSize = data?.settingGeneral
    ? data?.settingGeneral.fileCapacity * 1024 * 1024
    : MAX_QUIZ_FILE_UPLOAD_SIZE;
  // audio file data
  const [audioURL, setAudioURL] = useState<string>("");
  const [uploading, setUploading] = useState<boolean>(false);

  // control
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const [mode, setMode] = useState<"begin" | "recording" | "view" | "error">(
    "begin"
  );
  const [time, setTime] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);

  const blockId = toNumber(useKeyword("blockId"));
  const currentScreen = useKeyword("screen");
  const isSequentiallyQuiz = configs?.generalConfig?.sequentiallyQuiz;
  const [canStart, setCanStart] = useState<boolean>(false);

  useEffect(() => {
    // Xử lý câu hỏi có đếm thời gian riêng
    if (currentScreen === "train") {
      if (!isEmpty(data) && isSequentiallyQuiz && initialTime) {
        const startSingle = async () => {
          try {
            setLoading(true);
            await startSingleQuiz({
              blockId: blockId,
              quizId: data?.id,
            });
          } catch (err: any) {
          } finally {
            setCanStart(true);
            setLoading(false);
          }
        };
        startSingle();
      }
    }
  }, [isSequentiallyQuiz]);

  const startRecording = async () => {
    setMode("recording");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        audioChunksRef.current.push(event.data);
        // Kiểm tra kích thước dữ liệu hiện tại
        const currentSize = audioChunksRef.current.reduce(
          (total, chunk) => total + chunk.size,
          0
        );
        if (currentSize >= maxSize) {
          stopRecording();
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/wav",
        });
        uploadAudio(audioBlob);
        const audioUrl = URL.createObjectURL(audioBlob);
        setAudioURL(audioUrl);
        audioChunksRef.current = [];
      };

      mediaRecorder.start();
    } catch (err) {
      console.error("Error accessing the microphone", err);
      setMode("error");
    }
  };

  const uploadAudio = async (audioBlob: Blob | null) => {
    if (!audioBlob) return;

    const formData = new FormData();
    formData.append("file", audioBlob);

    try {
      setUploading(true);
      const response = await uploadFileV2(formData);

      onChange({
        [`recorderMp3-${data?.id}-${QuestionTypeEnum.MP3}-${JSON_SUB_TYPE.ContentRequest}`]:
          response?.data?.filePath,
      });
    } catch (error) {
      console.error("Error uploading file:", error);
    } finally {
      setUploading(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();

      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream
          .getTracks()
          .forEach((track) => track.stop());
      }
    }
    setMode("view");
  };

  const handleDeleteRecording = () => {
    setAudioURL("");
    setMode("begin");
    onChange({
      [`recorderMp3-${data?.id}-${QuestionTypeEnum.MP3}-${JSON_SUB_TYPE.ContentRequest}`]:
        null,
    });
  };

  const handlePlay = (e: any) => {
    if (!isEmpty(e.target)) {
      const { duration, currentTime } = e.target;

      if (duration === Infinity) {
        // setLoading(true);
      } else if (
        isNumber(duration) &&
        isNumber(currentTime) &&
        duration !== Infinity
      ) {
        setTime(Math.floor(duration - currentTime));
        // setLoading(false);
      }
    }
  };

  const displayTime = () => {
    if (time > 0) {
      const minutes = Math.floor(time / 60);
      const seconds = Math.floor(time % 60);
      return `${minutes > 10 ? minutes : `0${minutes}`}:${
        seconds < 10 ? "0" + seconds : seconds
      }`;
    } else return "00:00";
  };

  useEffect(() => {
    if (!isEmpty(initialData?.answer[0])) {
      const getFile = async () => {
        setAudioURL(initialData?.answer[0]);
        setMode("view");
      };
      getFile();
    }
  }, []);

  useEffect(() => {
    return () => stopRecording();
  }, []);

  const renderContent = () => {
    switch (mode) {
      case "begin":
        return (
          <div className="begin-screen">
            <Button
              disabled={disabled}
              className="record-item mt-2"
              onClick={startRecording}
            >
              <div className="center">
                <MicroIcon />
                <span className="font-size-14 font-weight-6 ml-1">
                  {t("Bắt đầu ghi âm câu trả lời của bạn", "Bắt đầu ghi âm câu trả lời của bạn")}
                </span>
              </div>
            </Button>
          </div>
        );
      case "recording":
        return (
          <div className="micro-recording-screen center flex-column mt-3">
            <div className="record-micro micro-recording-animation">
              <MicroIcon />
            </div>

            <Tooltip title={t("Kết thúc", "Kết thúc")}>
              <Button
                className="mt-2 btn-primary btn-action"
                onClick={stopRecording}
              >
                <ExportIcon />
              </Button>
            </Tooltip>
          </div>
        );
      case "view":
        return (
          <div className="view-screen flex gap-32 align-center">
            <div className="audio-control">
              <H5AudioPlayer
                src={audioURL}
                className="audio-play"
                autoPlay={false}
                autoPlayAfterSrcChange={false}
                loop={false}
                showFilledVolume={false}
                showJumpControls={false}
                customAdditionalControls={[]}
                customIcons={{
                  play: <PlayIcon />,
                  pause: <PausePrimaryIcon />,
                }}
                onListen={handlePlay}
              />
              <div className="dispay-time text-secondary">{displayTime()}</div>
            </div>

            <Button
              disabled={disabled}
              className="font-size-14 text-primary font-weight-6 pointer"
              onClick={startRecording}
            >
              {t("Ghi âm lại", "Ghi âm lại")}
            </Button>
            <Button
              disabled={disabled}
              className="text-danger font-size-14 font-weight-6 pointer"
              onClick={handleDeleteRecording}
            >
              {t("Xoá", "Xoá")}
            </Button>
          </div>
        );
      case "error":
        return (
          <div className="w-full center">
            <EmptyComponent description={t("Không tìm thấy thiết bị được yêu cầu", "Không tìm thấy thiết bị được yêu cầu")} />
          </div>
        );
      default:
        break;
    }
  };

  return (
    <div>
      <Spin spinning={uploading || loading}>
        <div
          className="question-header mb-3"
          style={{
            backgroundColor: "#f9fafb",
            borderRadius: "8px",
            padding: "15px",
          }}
        >
          {configs?.actionConfig?.timeForQuiz &&
          configs?.generalConfig?.sequentiallyQuiz ? (
            <div className="question-time flex align-center gap-8">
              <HoursGlassIcon />
              <div className="text-primary font-size-14 line-height-20 font-weight-6 flex align-center gap-8">
                {t("Trả lời câu hỏi trong:", "Trả lời câu hỏi trong:")}
                <Countdown
                  pause={pause}
                  initialTime={canStart ? toNumber(initialTime) : 0}
                  onFinish={() => {
                    if (!uploading) {
                      onFinish();
                      stopRecording();
                    }
                  }}
                />
              </div>
            </div>
          ) : (
            ""
          )}
          <div className="question-title" style={{ marginBottom: 0 }}>
            {hiddenIndex ? null : (
              <Flex
                justify="space-between"
                align="center"
                className="font-size-16 line-height-24 font-weight-6"
              >
                {data?.settingGeneral?.requiredAnswer ? (
                  <span className="text-danger">*</span>
                ) : (
                  ""
                )}
                <div className="flex-1 ml-1">
                  {t("Câu hỏi", "Câu hỏi")} {toNumber(data?.index) + 1}:{" "}
                </div>
                {configs?.generalConfig?.showQuizScore && (
                  <span className="total-point font-size-14">
                    {`(${data?.totalPoints || 0} điểm)`}
                  </span>
                )}
              </Flex>
            )}
            <Flex align="flex-start" gap={8}>
              <QuestionContentTitle content={data?.question as string} />
              {data?.settingHint?.isDisplayInstruction && data?.isShowHint ? (
                <Tooltip
                  placement="bottom"
                  title={
                    <div>
                      {data?.settingHint?.content ? (
                        <p className="text-14 font-weight-5">
                          {t("Hướng dẫn:", "Hướng dẫn:")} {data?.settingHint?.content}
                        </p>
                      ) : (
                        "Không có hướng dẫn"
                      )}

                      {data?.settingHint?.schema ? (
                        <p className="font-size-14 mt-2">
                          {t("Đường dẫn tham khảo:", "Đường dẫn tham khảo:")}{" "}
                          <Link
                            target="_blank"
                            to={`${data?.settingHint?.uri}${data?.settingHint?.schema}`}
                          >
                            {`${data?.settingHint?.uri}${data?.settingHint?.schema}`}
                          </Link>
                        </p>
                      ) : (
                        ""
                      )}
                    </div>
                  }
                >
                  <span className="quest-icon text-secondary w-max-content">
                    <QuestionMarkIcon />
                  </span>
                </Tooltip>
              ) : (
                ""
              )}
            </Flex>
          </div>
        </div>
        {renderContent()}
      </Spin>
    </div>
  );
}

export default Mp3;
